import { Base, GQL } from "@binale-tech/shared";

import Category from "../../models/Category";
import Creditor, { Debitor } from "../../models/Creditor";
import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import Tag from "../../models/Tag";
import { CompanyContext, YearPeriodContext } from "./../CompanyContext";
import {
    categoryDelete,
    categorySave,
    creditorCreate,
    creditorDelete,
    creditorUpdate,
    debitorCreate,
    debitorDelete,
    debitorUpdate,
    genericRecordTextTemplateSave,
    tagCreate,
    tagDelete,
    tagUpdate,
} from "./mutations/kontoMutations.graphql";
import {
    refCategory,
    refCreditor,
    refDebitor,
    refRecordTextTemplates,
    refTag,
} from "scripts/api/firebase/firebaseRootRefs";
import { useGqlMutator } from "scripts/graphql/useGqlMutator";
import { child, DataSnapshot } from "firebase/database";
import { useCollectionListener } from "../hooks/useCollectionListener";
import { ProductAccessUtils } from "../../models/utils/ProductAccessUtils";

export type AccountMaps = {
    categoryOverridesMap: Map<string, Base.INumName>;
    creditorsMap: Map<string, Creditor>;
    debitorsMap: Map<string, Debitor>;
    tagsMap: Map<string, Tag>;
};
type KontoCtxData = {
    categoryOverrides: Base.INumName[];
    categoryOverridesAllMap: Map<string, Base.INumName[]>;
    creditors: Creditor[];
    creditorsAllMap: Map<string, Creditor[]>;
    debitors: Debitor[];
    debitorsAllMap: Map<string, Creditor[]>;
    tags: Tag[];
    tagsAllMap: Map<string, Tag[]>;
    accountMapsAllYears: Map<number, AccountMaps>;
    recordTextTemplates: GQL.IGenericRecordTextTemplate[];
    isLoaded?: boolean;
};
const getDefaultAccountsMap = (): AccountMaps => ({
    categoryOverridesMap: new Map(),
    creditorsMap: new Map(),
    debitorsMap: new Map(),
    tagsMap: new Map(),
});
const getDefaultValue = (): KontoCtxData => ({
    categoryOverrides: [],
    categoryOverridesAllMap: new Map(),
    creditors: [],
    creditorsAllMap: new Map(),
    debitors: [],
    debitorsAllMap: new Map(),
    tags: [],
    tagsAllMap: new Map(),
    accountMapsAllYears: new Map(),
    recordTextTemplates: [],
    isLoaded: false,
});
export const KontoContext = React.createContext<KontoCtxData>(getDefaultValue());

type KontoController = {
    categoryOverrideSave: (v: Base.INumName) => Promise<void>;
    categoryOverrideDelete: (id: string, num: string) => Promise<void>;
    creditorSave: (v: Creditor) => Promise<void>;
    creditorDelete: (id: string, num: string) => Promise<void>;
    debSave: (v: Debitor) => Promise<void>;
    debDelete: (id: string, num: string) => Promise<void>;
    tagSave: (v: Tag) => Promise<void>;
    tagDelete: (id: string, num: string) => Promise<void>;
    recordTextTemplateSave: (v: Omit<GQL.IGenericRecordTextTemplate, "companyId">) => Promise<void>;
};
export const KontoControlContext = React.createContext<KontoController>({
    categoryOverrideSave: () => Promise.resolve(),
    categoryOverrideDelete: () => Promise.resolve(),
    creditorSave: () => Promise.resolve(),
    creditorDelete: () => Promise.resolve(),
    debSave: () => Promise.resolve(),
    debDelete: () => Promise.resolve(),
    tagSave: () => Promise.resolve(),
    tagDelete: () => Promise.resolve(),
    recordTextTemplateSave: () => Promise.resolve(),
});

const toMap = <T extends Base.INumName>(vs: T[]): Map<string, T> => {
    const map = new Map<string, T>();
    vs.forEach(v => {
        map.set(v.num, v);
    });
    return map;
};

export const EntitiesProvider: React.FC<React.PropsWithChildren> = props => {
    const { companyGQL, isLoaded: isCompanyLoaded } = React.use(CompanyContext);
    const companyId = companyGQL?.id;
    const { year } = React.use(YearPeriodContext);
    const mutator = useGqlMutator();
    const [value, setValue] = useState<KontoCtxData>(getDefaultValue());

    const shouldSkipLoad = useMemo(() => !ProductAccessUtils.hasCompanyAccounting(companyGQL), [companyGQL]);

    const categoriesAllRef = useMemo(() => child(refCategory, `${companyId}`), [companyId]);
    const categoriesAllInitializer = useCallback(
        (snap: DataSnapshot) => {
            const yearConfig = companyGQL?.accountingConfigs?.find(v => v.year === Number(snap.key));
            return Object.entries(snap.val())
                .map(([key, value]) => {
                    const { num, name, marked } = value as any;
                    return Category.unserialize({ num, name, marked, key }).fixNum(yearConfig?.kontoExt ?? 0);
                })
                .sort((a, b) => Number(a.num) - Number(b.num));
        },
        [companyGQL?.accountingConfigs]
    );
    const { state: categoriesAllMap, isLoaded: isLoadedCat } = useCollectionListener<Base.IExtNum[]>(
        categoriesAllRef,
        categoriesAllInitializer,
        shouldSkipLoad
    );

    const debitorsAllRef = useMemo(() => child(refDebitor, `${companyId}`), [companyId]);
    const debitorsAllInitializer = useCallback((snap: DataSnapshot) => {
        return Object.values(snap.val())
            .map(v => Debitor.unserialize(v as any))
            .sort((a, b) => Number(a.num) - Number(b.num));
    }, []);
    const { state: debitorsAllMap, isLoaded: isLoadedDeb } = useCollectionListener<Debitor[]>(
        debitorsAllRef,
        debitorsAllInitializer,
        shouldSkipLoad
    );

    const creditorsAllRef = useMemo(() => child(refCreditor, `${companyId}`), [companyId]);
    const creditorsAllInitializer = useCallback((snap: DataSnapshot) => {
        return Object.values(snap.val())
            .map(v => Creditor.unserialize(v as any))
            .sort((a, b) => Number(a.num) - Number(b.num));
    }, []);
    const { state: creditorsAllMap, isLoaded: isLoadedCred } = useCollectionListener<Creditor[]>(
        creditorsAllRef,
        creditorsAllInitializer,
        shouldSkipLoad
    );

    const tagsAllRef = useMemo(() => child(refTag, `${companyId}`), [companyId]);
    const tagsAllInitializer = useCallback((snap: DataSnapshot) => {
        return Object.values(snap.val())
            .map(v => Tag.unserialize(v as any))
            .sort((a, b) => Number(a.num) - Number(b.num));
    }, []);
    const { state: tagsAllMap, isLoaded: isLoadedTags } = useCollectionListener<Tag[]>(
        tagsAllRef,
        tagsAllInitializer,
        shouldSkipLoad
    );

    const textTemplatesRef = useMemo(() => child(refRecordTextTemplates, `${companyId}`), [companyId]);
    const textTemplatesInitializer = useCallback((snap: DataSnapshot) => {
        return snap.val() as GQL.IGenericRecordTextTemplate;
    }, []);
    const { list: recordTextTemplates } = useCollectionListener<GQL.IGenericRecordTextTemplate>(
        textTemplatesRef,
        textTemplatesInitializer,
        shouldSkipLoad
    );

    const aggregatedValue: KontoCtxData = React.useMemo(() => {
        const categoriesList = categoriesAllMap.get(String(year)) ?? [];
        const creditorsList = creditorsAllMap.get(String(year)) ?? [];
        const debitorsMap = debitorsAllMap.get(String(year)) ?? [];
        const tagsList = tagsAllMap.get(String(year)) ?? [];

        const accountMapsAllYears = new Map<number, AccountMaps>();
        (companyGQL?.accountingConfigs ?? []).forEach(c => accountMapsAllYears.set(c.year, getDefaultAccountsMap()));

        if (accountMapsAllYears.size) {
            for (const [yearStr, cats] of categoriesAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).categoryOverridesMap = toMap(cats);
                }
            }
            for (const [yearStr, creds] of creditorsAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).creditorsMap = toMap(creds);
                }
            }
            for (const [yearStr, debs] of debitorsAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).debitorsMap = toMap(debs);
                }
            }
            for (const [yearStr, tags] of tagsAllMap.entries()) {
                if (accountMapsAllYears.has(Number(yearStr))) {
                    accountMapsAllYears.get(Number(yearStr)).tagsMap = toMap(tags);
                }
            }
        }
        return {
            categoryOverrides: [...categoriesList],
            creditors: [...creditorsList],
            debitors: [...debitorsMap],
            tags: [...tagsList],
            categoryOverridesAllMap: categoriesAllMap,
            creditorsAllMap,
            debitorsAllMap,
            tagsAllMap,
            accountMapsAllYears,
            recordTextTemplates,
            isLoaded: isLoadedCat && isLoadedCred && isLoadedDeb && isLoadedTags && isCompanyLoaded,
        };
    }, [
        categoriesAllMap,
        year,
        creditorsAllMap,
        debitorsAllMap,
        tagsAllMap,
        companyGQL?.accountingConfigs,
        recordTextTemplates,
        isLoadedCat,
        isLoadedCred,
        isLoadedDeb,
        isLoadedTags,
        isCompanyLoaded,
    ]);

    const deferredValue = useDeferredValue(aggregatedValue);
    useEffect(() => {
        if (deferredValue.isLoaded) {
            setValue(deferredValue);
        }
        return () => {
            setValue(getDefaultValue());
        };
    }, [deferredValue]);

    const kontoControl: KontoController = React.useMemo(
        () => ({
            categoryOverrideSave: async category => {
                await mutator.mutate<"categorySave", GQL.ICategorySaveInput>({
                    mutation: categorySave,
                    input: {
                        companyId,
                        name: category.name,
                        accountNumString: category.num,
                        year,
                    },
                });
            },
            categoryOverrideDelete: async (id, num) => {
                await mutator.mutate<"categoryDelete", GQL.ICompanyNumEntityDeleteInput>({
                    mutation: categoryDelete,
                    input: {
                        companyId,
                        id,
                        year,
                        num,
                    },
                });
            },
            creditorSave: async creditor => {
                const { name, description } = creditor;
                const data = {
                    name,
                    description,
                    companyId,
                };
                if (creditor.key) {
                    await mutator.mutate<"creditorUpdate", GQL.ICredDebUpdateInput>({
                        mutation: creditorUpdate,
                        input: {
                            ...data,
                            id: creditor.key,
                            num: creditor.num,
                            year,
                        },
                    });
                } else {
                    await mutator.mutate<"creditorCreate", GQL.ICredDebCreateInput>({
                        mutation: creditorCreate,
                        input: {
                            ...data,
                            year,
                            accountNum: +creditor.num,
                        },
                    });
                }
            },
            creditorDelete: async (id, num) => {
                await mutator.mutate<"creditorDelete", GQL.ICompanyNumEntityDeleteInput>({
                    mutation: creditorDelete,
                    input: {
                        companyId,
                        id,
                        year,
                        num,
                    },
                });
            },
            debSave: async debitor => {
                const { name, description } = debitor;
                const data = {
                    name,
                    description,
                    companyId,
                };
                if (debitor.key) {
                    await mutator.mutate<"debitorUpdate", GQL.ICredDebUpdateInput>({
                        mutation: debitorUpdate,
                        input: {
                            ...data,
                            id: debitor.key,
                            num: debitor.num,
                            year,
                        },
                    });
                } else {
                    await mutator.mutate<"debitorCreate", GQL.ICredDebCreateInput>({
                        mutation: debitorCreate,
                        input: {
                            ...data,
                            accountNum: +debitor.num,
                            year,
                        },
                    });
                }
            },
            debDelete: async (id, num) => {
                await mutator.mutate<"debitorDelete", GQL.ICompanyNumEntityDeleteInput>({
                    mutation: debitorDelete,
                    input: {
                        companyId,
                        id,
                        year,
                        num,
                    },
                });
            },
            tagSave: async tag => {
                if (tag.key) {
                    await mutator.mutate<"tagUpdate", GQL.ITagUpdateInput>({
                        mutation: tagUpdate,
                        input: {
                            companyId,
                            id: tag.key,
                            num: tag.num,
                            name: tag.name,
                            year,
                        },
                    });
                } else {
                    await mutator.mutate<"tagCreate", GQL.ITagCreateInput>({
                        mutation: tagCreate,
                        input: {
                            companyId,
                            name: tag.name,
                            num: tag.num,
                            year,
                        },
                    });
                }
            },
            tagDelete: async (id, num) => {
                await mutator.mutate<"tagDelete", GQL.ICompanyNumEntityDeleteInput>({
                    mutation: tagDelete,
                    input: {
                        companyId,
                        id,
                        year,
                        num,
                    },
                });
            },
            recordTextTemplateSave: async v => {
                await mutator.mutate<"genericRecordTextTemplateSave", GQL.IGenericRecordTextTemplateSaveInput>({
                    mutation: genericRecordTextTemplateSave,
                    input: {
                        companyId,
                        ...v,
                        bankIds: v.bankIds ?? [],
                        kbIds: v.kbIds ?? [],
                    },
                });
            },
        }),
        [companyId, mutator, year]
    );
    return (
        <KontoContext value={value}>
            <KontoControlContext value={kontoControl}>{props.children}</KontoControlContext>
        </KontoContext>
    );
};
